import React, { useState, useEffect } from 'react';
import { apiRequest } from '../config/api';

interface Carga {
  id: string;
  ordem: string;
  status: string;
  prioridade: string;
  fornecedor: string;
  mercadorias?: string;
  tipo_carga?: string;
  tipo_produto?: string;
  conferente?: string;
  ocorrencias?: any[];
  data_programada: string;
  status_d2: string;
  pre_entrada_d2: boolean;
  usuario_status_d2?: string;
  data_status_d2?: string;
  usuario_pre_entrada_d2?: string;
  data_pre_entrada_d2?: string;
  dados_logisticos_d2?: boolean;
  usuario_dados_logisticos_d2?: string;
  data_dados_logisticos_d2?: string;
  pedido_digitado_d2?: boolean;
  usuario_pedido_digitado_d2?: string;
  data_pedido_digitado_d2?: string;
  solicitacoes_agendamento?: {
    tipo_carga?: string;
    tipo_produto?: string;
    mercadoria_descricao?: string;
    observacoes?: string;
    documentos?: Array<{
      id?: string;
      nome: string;
      url?: string;
      storagePath?: string;
      tipo?: string;
      tamanho?: number;
    }>;
  };
}

interface CheckboxD2Props {
  filial: string;
}

const CheckboxD2: React.FC<CheckboxD2Props> = ({ filial }) => {
  const [cargas, setCargas] = useState<Carga[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataSelecionada, setDataSelecionada] = useState(new Date().toISOString().split('T')[0]);
  const [modalDocumentos, setModalDocumentos] = useState<{ aberto: boolean; documentos: any[]; titulo: string }>({
    aberto: false,
    documentos: [],
    titulo: ''
  });

  const carregarCargas = async () => {
    try {
      setLoading(true);
      const result = await apiRequest(`/cargas?filial=${filial}&data_inicio=${dataSelecionada}&data_fim=${dataSelecionada}`);
      setCargas(result.data || []);
    } catch (error) {
      console.error('Erro ao carregar cargas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarCargas();
  }, [filial, dataSelecionada]);

  const formatarData = (dataStr: string) => {
    // Converter string para Date usando fuso horário local para evitar problemas de UTC
    if (dataStr.includes('T')) {
      const [ano, mes, dia] = dataStr.split('T')[0].split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } else {
      const [ano, mes, dia] = dataStr.split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  };

  const formatarDataHora = (dataStr?: string) => {
    if (!dataStr) return '-';
    const data = new Date(dataStr);
    return data.toLocaleString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStatusChange = async (id: string, novoStatus: 'pendente' | 'em_andamento' | 'feito') => {
    try {
      await apiRequest(`/cargas/${id}/d2`, {
        method: 'PATCH',
        body: JSON.stringify({ status_d2: novoStatus }),
      });
      await carregarCargas();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status');
    }
  };

  const handlePreEntradaChange = async (id: string, preEntrada: boolean) => {
    try {
      await apiRequest(`/cargas/${id}/d2`, {
        method: 'PATCH',
        body: JSON.stringify({ pre_entrada_d2: preEntrada }),
      });
      await carregarCargas();
    } catch (error) {
      console.error('Erro ao atualizar pré entrada:', error);
      alert('Erro ao atualizar pré entrada');
    }
  };

  const handleDadosLogisticosChange = async (id: string, dadosLogisticos: boolean) => {
    try {
      await apiRequest(`/cargas/${id}/d2`, {
        method: 'PATCH',
        body: JSON.stringify({ dados_logisticos_d2: dadosLogisticos }),
      });
      await carregarCargas();
    } catch (error) {
      console.error('Erro ao atualizar dados logísticos:', error);
      alert('Erro ao atualizar dados logísticos');
    }
  };

  const handlePedidoDigitadoChange = async (id: string, pedidoDigitado: boolean) => {
    try {
      await apiRequest(`/cargas/${id}/d2`, {
        method: 'PATCH',
        body: JSON.stringify({ pedido_digitado_d2: pedidoDigitado }),
      });
      await carregarCargas();
    } catch (error) {
      console.error('Erro ao atualizar pedido digitado:', error);
      alert('Erro ao atualizar pedido digitado');
    }
  };

  const abrirDocumentos = (carga: Carga) => {
    const docs = carga.solicitacoes_agendamento?.documentos || [];
    const docsArray = typeof docs === 'string' ? JSON.parse(docs) : docs;
    setModalDocumentos({
      aberto: true,
      documentos: docsArray,
      titulo: `Documentos - ${carga.fornecedor || 'Carga'}`
    });
  };

  const abrirDocumento = (doc: any) => {
    const url = doc.url || doc.storagePath;
    if (url) window.open(url, '_blank');
    else alert('URL do documento não disponível');
  };

  const getPrioridadeBadge = (prioridade: string) => {
    switch (prioridade) {
      case 'Alta':
        return <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">Alta</span>;
      case 'Baixa':
        return <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Baixa</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">Normal</span>;
    }
  };

  const contarPorStatus = (itens: Carga[]) => {
    return {
      feito: itens.filter(i => i.status_d2 === 'feito').length,
      em_andamento: itens.filter(i => i.status_d2 === 'em_andamento').length,
      pendente: itens.filter(i => i.status_d2 === 'pendente').length,
      pre_entrada: itens.filter(i => i.pre_entrada_d2).length,
      dados_logisticos: itens.filter(i => i.dados_logisticos_d2).length,
      pedido_digitado: itens.filter(i => i.pedido_digitado_d2).length
    };
  };

  if (loading) {
    return <div className="p-6">Carregando...</div>;
  }

  const cargasAgrupadas: Record<string, Carga[]> = {};
  cargas.forEach(carga => {
    const data = carga.data_programada || 'Sem data';
    if (!cargasAgrupadas[data]) {
      cargasAgrupadas[data] = [];
    }
    cargasAgrupadas[data].push(carga);
  });

  return (
    <>
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Checkbox D+2</h2>
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Data:</label>
          <input
            type="date"
            value={dataSelecionada}
            onChange={(e) => setDataSelecionada(e.target.value)}
            className="border rounded px-3 py-2"
          />
        </div>
      </div>

      {cargas.length === 0 ? (
        <div className="bg-white p-6 rounded-lg shadow text-center text-gray-500">
          Nenhuma carga encontrada para a data selecionada.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(cargasAgrupadas)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([data, itens]) => {
              const contagem = contarPorStatus(itens);
              return (
                <div key={data} className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="bg-gradient-to-r from-slate-900 to-blue-600 text-white p-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl font-bold">{formatarData(data)}</h3>
                      <div className="flex gap-4 text-sm">
                        <span>✓ {contagem.feito} feitos</span>
                        <span>⟳ {contagem.em_andamento} em andamento</span>
                        <span>○ {contagem.pendente} pendentes</span>
                        <span>📥 {contagem.pre_entrada} pré-entrada</span>
                        <span>📦 {contagem.dados_logisticos} dados logísticos</span>
                        <span>📝 {contagem.pedido_digitado} pedido digitado</span>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Prioridade</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Fornecedor</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Mercadorias</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status D+2</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Pré-entrada</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">DADOS LOGÍSTICOS</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">PEDIDO DIGITADO</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Documentos</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Responsável</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {itens.map((carga) => (
                          <tr key={carga.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm">{getPrioridadeBadge(carga.prioridade)}</td>
                            <td className="px-4 py-3 text-sm">{carga.fornecedor || '-'}</td>
                            <td className="px-4 py-3 text-sm">
                              {carga.solicitacoes_agendamento?.mercadoria_descricao || carga.solicitacoes_agendamento?.observacoes || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <select
                                value={carga.status_d2}
                                onChange={(e) => handleStatusChange(carga.id, e.target.value as any)}
                                className="border rounded px-2 py-1 text-sm"
                              >
                                <option value="pendente">○ Pendente</option>
                                <option value="em_andamento">⟳ Em andamento</option>
                                <option value="feito">✓ Feito</option>
                              </select>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={carga.pre_entrada_d2}
                                  onChange={(e) => handlePreEntradaChange(carga.id, e.target.checked)}
                                  className="w-4 h-4"
                                />
                                <span>{carga.pre_entrada_d2 ? '✓' : '○'}</span>
                              </label>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={carga.dados_logisticos_d2}
                                  onChange={(e) => handleDadosLogisticosChange(carga.id, e.target.checked)}
                                  className="w-4 h-4"
                                />
                                <span>{carga.dados_logisticos_d2 ? '✓' : '○'}</span>
                              </label>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={carga.pedido_digitado_d2}
                                  onChange={(e) => handlePedidoDigitadoChange(carga.id, e.target.checked)}
                                  className="w-4 h-4"
                                />
                                <span>{carga.pedido_digitado_d2 ? '✓' : '○'}</span>
                              </label>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {(() => {
                                const docs = carga.solicitacoes_agendamento?.documentos;
                                const docsArray = docs ? (typeof docs === 'string' ? JSON.parse(docs) : docs) : [];
                                return docsArray.length > 0 ? (
                                  <button
                                    onClick={() => abrirDocumentos(carga)}
                                    className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-md font-medium transition-colors"
                                  >
                                    📄 {docsArray.length} doc{docsArray.length > 1 ? 's' : ''}
                                  </button>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {carga.usuario_status_d2 && (
                                <div>
                                  <div className="font-medium">{carga.usuario_status_d2}</div>
                                  <div className="text-xs text-gray-500">{formatarDataHora(carga.data_status_d2)}</div>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>

    {/* Modal de Documentos */}

    {modalDocumentos.aberto && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">📄 {modalDocumentos.titulo}</h3>
            <button
              onClick={() => setModalDocumentos({ aberto: false, documentos: [], titulo: '' })}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>
          <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
            {modalDocumentos.documentos.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">Nenhum documento encontrado.</p>
            ) : (
              modalDocumentos.documentos.map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-blue-500 text-lg flex-shrink-0">📄</span>
                    <span className="text-sm text-gray-700 truncate">{doc.nome || `Documento ${idx + 1}`}</span>
                  </div>
                  <button
                    onClick={() => abrirDocumento(doc)}
                    className="flex-shrink-0 ml-2 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-md transition-colors"
                  >
                    Abrir
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="p-4 border-t text-right">
            <button
              onClick={() => setModalDocumentos({ aberto: false, documentos: [], titulo: '' })}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm px-4 py-2 rounded-lg transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CheckboxD2;
